<template>
  <div class="wrapper">
    <div v-if="isFocused" :class="{ title: !isFilled, title_focus: isFocused }">
      {{ props.placeholder }}
    </div>
    <div
      v-else-if="!isFocused && isFilled && !errorMessage && !props.disabled"
      class="title"
    >
      {{ props.placeholder }}
    </div>

    <div
      v-else-if="!isFocused && isFilled && !errorMessage && props.disabled"
      class="titleDisabled"
    >
      {{ props.placeholder }}
    </div>
    <div v-else-if="errorMessage" class="titleError">
      {{ props.placeholder }}
    </div>
    <Money3Component
      v-bind="config || $attrs"
      id="input"
      v-model.number="inputValue"
      type="number"
      class="input"
      :class="{
        'placeholder-text': !inputValue,
        focused: isFocused,
        inputError: errorMessage,
      }"
      :name="field"
      :disabled="disabled"
      :placeholder="placeholderHidden"
      @change="updateValue"
      v-on="validationListeners"
      @focus="handleInputFocus"
      @focusin="handleInputFocus"
      @focusout="handleBlurOut"
      @mousedown.stop="handleClick"
      :autocomplete="autocomplete"
      @paste="handlePaste"
      :model-modifiers="{ number: true }"
      :maxlength="maxLength"
    />
    <!-- @mouseleave="handleBlurOut" -->
    <!-- <pre class="help" v-if="!errorMessage"> {{ errorMessage }}</pre> -->
    <pre class="help"> {{ errorMessage }}</pre>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, computed, onMounted } from "vue";
import { useField, Field } from "vee-validate";
// @ts-ignore
import { Money3Component } from "v-money3";

const props = defineProps({
  id: {
    type: String,
    // required: true,
  },
  autocomplete: {
    type: String,
  },
  validateRule: {
    type: String,
  },
  modelValue: {
    type: Number,
    default: 0,
  },
  type: {
    type: String,
    default: "text",
  },
  field: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: "",
  },
  minLength: {
    type: Number,
  },
  maxLength: {
    type: Number,
    default: 50,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: true,
  },
  validationType: {
    type: String,
    default: "",
  },
  class: {
    type: String,
  },
});

const config = ref({
  masked: true,
  prefix: "MYR ",
  suffix: "",
  thousands: ",",
  decimal: ".",
  precision: 2,
  disableNegative: true,
  disabled: false,
  min: "0",
  max: "9999999.99",
  allowBlank: true,
  minimumNumberOfCharacters: 0,
  shouldRound: false,
  focusOnRight: false,
  placeholder: props.placeholder,
  minLength: 4,
});

const isFocused = ref(false);
const isFilled = ref(false);
const inputValue = ref(props.modelValue);

if (props.modelValue) {
  isFilled.value = true;
}

const { value, errorMessage, handleBlur } = useField(
  () => props.field,
  props.validateRule,
  {
    validateOnValueUpdate: false,
  }
);
if (props.modelValue != 0) {
  value.value = props.modelValue;
}
const disableAutoFocus = ref(false);
const handleInputFocus = () => {
  if (config) {
    config.value.allowBlank = false;
  }
  disableAutoFocus.value = false;
  isFocused.value = true;
  isFilled.value = false;
};

const emits = defineEmits(["update:modelValue"]);
const updateValue = (event: InputEvent) => {
  //   const inputText = (event.target as HTMLInputElement).value;
  //   const isNumericInput = /^\d+$/.test(inputText);

  emits("update:modelValue", inputValue);
  inputValue.value = props.modelValue;
};

const validationListeners = {
  blur: (evt: Event | undefined) => handleBlur(evt, true),
};

const handleClick = () => {
  setTimeout(() => {
    isFocused.value = true;
    isFilled.value = true;
  }, 0);
};

const test = ref("");

const handleBlurOut = () => {
  if (inputValue.value == 0) {
    isFilled.value = false;
    isFocused.value = false;
  } else {
    isFilled.value = true;
    isFocused.value = false;
  }
};

const placeholderHidden = computed(() => {
  return isFocused.value ? "" : props.placeholder;
});

const handlePaste = async (event: ClipboardEvent) => {
  try {
    let pastedText = "";

    if (navigator.clipboard) {
      pastedText = await navigator.clipboard.readText();
    } else {
      pastedText = event.clipboardData?.getData("text") || "";
    }

    if (!/^\d+$/.test(pastedText)) {
      event.preventDefault();
    }
  } catch (error) {
    console.error("Error reading clipboard:", error);
  }
};

watch(inputValue, (newValue) => {
  value.value = newValue;
});
watch(
  () => props.modelValue,
  (newModelValue) => {
    if (newModelValue != 0) {
      value.value = newModelValue;
      isFilled.value = true;
    } else {
      isFilled.value = false;
      value.value = newModelValue;
    }
  }
);
</script>

<style scoped>
.wrapper {
  position: relative;
}

.help {
  color: red;
  margin-bottom: 0px;
  margin-top: 8px;
  font: 400 16px/130% "Outfit", sans-serif;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.input {
  border: 2px solid #dbdfea;
  padding: 14px 31px 14px 22px;
  border-radius: 10px;
  width: 100%;
  color: rgba(0, 0, 0, 0.87);
  font: 400 16px/130% "Outfit", sans-serif;
  outline-color: #ac2887;
}

.inputError {
  border: 2px solid red;
  padding: 14px 31px 14px 22px;
  border-radius: 10px;
  width: 100%;
  color: rgba(0, 0, 0, 0.87);
  font: 400 16px/130% "Outfit", sans-serif;
  outline-color: #ac2887;
}

.input.focused {
  border-color: #ac2887;
  outline: none;
}

.input:focus {
  box-shadow: none;
  outline: none;
}

.input::placeholder {
  color: var(--black-low-38, rgba(0, 0, 0, 0.38));
  font: var(--subtitle-1-medium, 500 16px/130% "Outfit", sans-serif);
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px white inset !important;
}

input:not(:placeholder-shown) {
  background-color: #ffffff !important;
}

input:placeholder-shown {
  background-color: #ffffff !important;
}

.title {
  position: absolute;
  top: -8px;
  left: 15px;
  color: rgba(0, 0, 0, 0.38);
  font: 500 12px/130% "Outfit", sans-serif;
  background: white;
  padding: 0 5px;
}

.titleDisabled {
  position: absolute;
  top: -8px;
  left: 15px;
  color: rgba(0, 0, 0, 0.38);
  font: 500 12px/130% "Outfit", sans-serif;
  background: linear-gradient(to bottom, white 52%, #dbdfea 48%);
  padding: 0 5px;
}

.titleError {
  position: absolute;
  top: -8px;
  left: 15px;
  color: red;
  font: 500 12px/130% "Outfit", sans-serif;
  background-color: white;
  padding: 0 5px;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes slideOut {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(100%);
  }
}

.title_focus {
  position: absolute;
  top: -8px;
  left: 15px;
  color: #ac2887;
  font: 500 12px/130% "Outfit", sans-serif;
  background-color: white;
  padding: 0 5px;
  opacity: 0;
  transform: translateY(100%);

  /* Animation */
  animation: slideIn 0.3s ease-out forwards;
}

.title_remove_focus {
  /* Animation */
  animation: slideOut 0.3s ease-out forwards;
}

.input[disabled] {
  background-color: #dbdfea !important;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
  appearance: none;
}
</style>
